import { Checkbox } from '@mui/material';
import MuiAutocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import MuiChip from '@mui/material/Chip';
import { useProjects } from '@si/hooks';
import type { Project } from '@si/types';
import { useTranslate } from '@si/utils';
import { useId, useMemo } from 'react';

import { TextInput } from '../Input/TextInput';
import { Stack } from '../Stack/Stack';
interface Props {
  projectIds: number[];
  onChange: (projectIds: number[]) => void;
  inputWidth?: '10rem' | '15rem' | '20rem' | '25rem' | '40rem' | '100%';
  label?: string;
  required?: boolean;
  overrideVisited?: boolean;
}

export function ProjectsMultiSelect({
  projectIds,
  onChange,
  inputWidth = '100%',
  label,
  required = false,
  overrideVisited = false,
}: Props) {
  const { t, translate } = useTranslate();
  const { projects, isLoading } = useProjects();
  const generatedId = useId();

  const uniqueProjects = useMemo(() => {
    const seenIds = new Set<number>();
    const seenNames = new Set<string>();
    return projects.filter((project) => {
      const nameKey = project.name.trim().toLowerCase();
      if (seenIds.has(project.projectId) || seenNames.has(nameKey)) {
        return false;
      }
      seenIds.add(project.projectId);
      seenNames.add(nameKey);
      return true;
    });
  }, [projects]);

  const selectedProjects = useMemo(
    () => uniqueProjects.filter((project) => projectIds.includes(project.projectId)),
    [uniqueProjects, projectIds]
  );

  const showError = required && projectIds.length === 0 && overrideVisited;
  const errorText = showError
    ? translate(t.fieldRequired, { field: label ?? translate(t.projects) })
    : '';

  const maxWidth = inputWidth === '10rem' || inputWidth === '15rem' ? '20rem' : inputWidth;
  const filterOptions = createFilterOptions<Project>({
    stringify: (option) => `${option.name} ${option.projectId}`,
    trim: true,
    ignoreAccents: true,
    ignoreCase: true,
  });

  return (
    <Stack gap="0.5rem">
      <MuiAutocomplete<Project, true, false, false>
        multiple
        disableCloseOnSelect
        fullWidth
        id={generatedId}
        options={uniqueProjects}
        filterOptions={filterOptions}
        value={selectedProjects}
        isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
        getOptionLabel={(option) => option.name}
        onChange={(_, value) => onChange((value ?? []).map((project) => project.projectId))}
        loading={isLoading}
        loadingText={translate(t.loading)}
        noOptionsText={translate(t.noProjects)}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox checked={selected} />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextInput
            {...params}
            errorText={errorText}
            id={generatedId}
            inputWidth={inputWidth}
            label={label ?? translate(t.projects)}
            onChange={params.inputProps.onChange}
            overrideVisited={overrideVisited}
            required={required}
            startAdornment={undefined}
            value={params.inputProps.value?.toString() ?? ''}
            slotProps={{
              input: {
                ...params.InputProps,
                sx: { py: '0!important' },
              },
              htmlInput: { ...params.inputProps, id: generatedId, name: generatedId },
            }}
          />
        )}
        sx={{ maxWidth }}
      />

      {selectedProjects.length > 0 && (
        <Stack direction="row" gap="0.5rem" flexWrap="wrap" maxWidth={maxWidth}>
          {selectedProjects.map((project) => (
            <MuiChip
              key={project.projectId}
              label={project.name}
              onDelete={() =>
                onChange(projectIds.filter((projectId) => projectId !== project.projectId))
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
